import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverSeawallRunningArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "seawall-running-tips";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park Seawall Loop",
    "section-2": "Coal Harbour Seawall",
    "section-3": "English Bay & Sunset Beach",
    "section-4": "False Creek Seawall Loop",
    "section-5": "Kitsilano to Spanish Banks",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References"
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/seawall.aspx",
      label: "City of Vancouver - Seawall Information",
    },
    {
      url: "https://www.destinationvancouver.com/activities/outdoors/stanley-park/seawall/",
      label: "Destination Vancouver - Seawall Guide",
    },
    {
      url: "https://rungoapp.com/route/vancouver-seawall/",
      label: "RunGo App - Vancouver Seawall Routes",
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park Seawall Loop Starting Point",
    },
    {
      id: "section-2",
      lat: 49.28917,
      lng: -123.11380,
      popupText: "Coal Harbour Seawall (Jack Poole Plaza)",
    },
    {
      id: "section-3",
      lat: 49.28627,
      lng: -123.14359,
      popupText: "English Bay Beach (Inukshuk Statue)",
    },
    {
      id: "section-4",
      lat: 49.27330,
      lng: -123.10380,
      popupText: "False Creek (Near Science World)",
    },
    {
      id: "section-5",
      lat: 49.27389,
      lng: -123.15511,
      popupText: "Kitsilano Beach Park Start",
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Seawall Secrets: Vancouver Running Tips</title>
        <meta
          name="description"
          content="Discover the best runs along Vancouver's iconic Seawall, including Stanley Park, Coal Harbour, and Kits Beach, with insider tips for every runner."
        />
        <meta
          name="keywords"
          content="Vancouver Seawall, running tips, Stanley Park, English Bay, Coal Harbour, Kitsilano, False Creek"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/seawall-running-tips.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Seawall Secrets: Vancouver Running Tips"
        />
        <meta
          property="og:description"
          content="Explore Vancouver’s famous Seawall for running. Find top routes, safety guidelines, FAQ, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/seawall-running-tips.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/seawall-running-tips.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Seawall Secrets: Vancouver Running Tips"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver’s famous Seawall for running. Find top routes, safety guidelines, FAQ, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/seawall-running-tips.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image
                  ? ref_data.image
                  : "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A runner's perspective along Vancouver's beautiful Seawall."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver’s Seawall is the world’s longest uninterrupted
              waterfront path, stretching nearly 28 km around downtown,
              Stanley Park, and beyond. The flat, scenic route offers
              breathtaking ocean vistas, lush forest views, and access to
              vibrant neighborhoods. Whether you’re a seasoned runner or
              just lacing up for a casual jog, the Seawall delivers an
              unforgettable experience. Below are insider tips, route ideas,
              local regulations, and answers to common FAQs to help you make
              the most of your run in this iconic setting.
            </p>

            {/* SECTION: Stanley Park Seawall Loop */}
            <h2 id="section-1">Stanley Park Seawall Loop (≈9 km)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park Loop Start</strong>{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Begin at the Stanley Park entrance near Coal Harbour. This
              9-kilometer loop treats you to panoramic water views on one
              side and a lush coastal rainforest on the other. You’ll pass
              iconic landmarks like Brockton Point Lighthouse, Siwash Rock,
              and the Totem Poles. The terrain is paved and nearly flat,
              making it accessible for all levels. Best run early morning or
              weekday evenings to avoid the heaviest foot traffic.
            </p>

            {/* SECTION: Coal Harbour Seawall */}
            <h2 id="section-2">Coal Harbour Seawall (≈2–3 km one-way)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coal Harbour (Jack Poole Plaza)</strong>{" "}
              <Marker
                coordY={49.28917}
                coordX={-123.11380}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – For a shorter, urban waterfront run, start at the Vancouver
              Convention Centre’s Jack Poole Plaza. Head west along Coal
              Harbour to Stanley Park, taking in city and mountain views.
              This route is bustling with locals, office workers on lunch
              break, and tourists. It’s an easy path that connects you
              directly to Stanley Park’s loop, offering plenty of benches
              and harbor-side coffee stops along the way.
            </p>

            {/* SECTION: English Bay & Sunset Beach */}
            <h2 id="section-3">English Bay & Sunset Beach (≈2.5 km one-way)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>English Bay Beach (Inukshuk Statue)</strong>{" "}
              <Marker
                coordY={49.28627}
                coordX={-123.14359}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This stretch of the Seawall is famous for its stunning
              sunsets and lively beach atmosphere. Begin near the Inukshuk
              statue at English Bay Beach and run southeast past Sunset
              Beach Park. You’ll enjoy open ocean vistas, people-watching,
              and the sound of waves. Evening runs here are particularly
              picturesque, though the path can be busy on warm weekends.
            </p>

            {/* SECTION: False Creek Seawall */}
            <h2 id="section-4">False Creek Seawall Loop (≈5–10 km)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>False Creek (Near Science World)</strong>{" "}
              <Marker
                coordY={49.27330}
                coordX={-123.10380}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – For a more urban skyline run, try the False Creek loop. It
              wraps around the inlet past Science World, BC Place, and
              Yaletown. You can extend the route by crossing bridges like
              Burrard or Cambie to create a loop of 5 to 10 km. This area is
              well-lit and equipped with plenty of water stations, washrooms,
              and cafes. It’s less crowded than Stanley Park but still
              bustling with life.
            </p>

            {/* SECTION: Kitsilano to Spanish Banks */}
            <h2 id="section-5">Kitsilano to Spanish Banks (≈5+ km one-way)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kitsilano Beach Park Start</strong>{" "}
              <Marker
                coordY={49.27389}
                coordX={-123.15511}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – For a longer, scenic adventure, run from Kits Beach through
              Jericho and Locarno Beaches, ending at Spanish Banks. This
              path boasts expansive views of the North Shore Mountains and
              the open bay. It’s quieter than the downtown stretches,
              providing a laid-back seaside atmosphere. You’ll find washrooms
              and water along the way, but plan for extra time if you venture
              into Spanish Banks, as it’s further west from the city center.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations" ref={(el) => setSpotRef("local-regulations", el)}>
              Local Regulations & Best Practices
            </h2>
            <p>
              Vancouver’s Seawall is a multi-use path shared by pedestrians,
              runners, and cyclists:
            </p>
            <ul>
              <li>
                <strong>Stay in Your Lane:</strong> Pedestrians and runners
                typically use the waterfront side, while bikes and skaters
                use the inside lane. Follow signage to avoid confusion.
              </li>
              <li>
                <strong>One-Way for Cyclists in Stanley Park:</strong> Bikes
                must go counter-clockwise around Stanley Park. Runners can
                go either direction, but be cautious if running clockwise.
              </li>
              <li>
                <strong>Yield & Courtesy:</strong> If overtaking someone,
                pass on the left and announce yourself. Stay right otherwise.
              </li>
              <li>
                <strong>Keep Dogs Leashed:</strong> Dogs must stay on-leash
                along the Seawall, with certain off-leash areas designated
                at nearby beaches.
              </li>
              <li>
                <strong>Night Running:</strong> Carry a light or wear
                reflective gear. Some sections are not well-lit after dark.
              </li>
            </ul>
            <p>
              Always check official resources for any temporary closures or
              updates, especially after storms.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. How long is the Vancouver Seawall?</h3>
              <p>
                The Seawall is about 22 km from the Convention Centre around
                Stanley Park to Kitsilano Beach. Extending to Spanish Banks
                adds another 6 km, totaling roughly 28 km.
              </p>

              <h3>2. When is the best time to run to avoid crowds?</h3>
              <p>
                Early mornings on weekdays are ideal for fewer crowds.
                Mid-day and weekends can be busy, especially in summer. If
                you enjoy a lively atmosphere, evenings offer gorgeous
                sunsets along English Bay.
              </p>

              <h3>3. Are there water fountains and restrooms available?</h3>
              <p>
                Yes. You’ll find washrooms and water stations at key points
                like Stanley Park (Second/Third Beach), English Bay Beach,
                Sunset Beach, Science World, and along beaches west of
                Kitsilano.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              Vancouver’s Seawall seamlessly blends urban charm with the
              beauty of the Pacific coast. From Stanley Park’s iconic loop
              to the laid-back beaches of Kitsilano and Spanish Banks, each
              route showcases a different slice of this vibrant city. By
              following the shared path rules and planning your run around
              quieter times, you’ll get the most out of this uniquely
              Canadian treasure. Lace up your shoes, hit the Seawall, and
              enjoy an unforgettable running experience on the waterfront!
            </p>

            {/* BOTTOM COMPONENT: references */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}